/**
 * useToast - Composable per notifiche toast
 * Versione pulita per Gleem Core
 */

import { ref, onBeforeUnmount } from 'vue'

interface Toast {
  id: string
  message: string
  type: 'success' | 'error' | 'warning' | 'info'
  duration: number
  createdAt: number
}

const toasts = ref<Toast[]>([])
const timers = new Map<string, number>()
const MAX_TOASTS = 5

function genId(): string {
  return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function useToast() {
  const remove = (id: string) => {
    const idx = toasts.value.findIndex(t => t.id === id)
    if (idx > -1) {
      toasts.value.splice(idx, 1)
    }
    const h = timers.get(id)
    if (h) {
      clearTimeout(h)
      timers.delete(id)
    }
  }

  const clearAll = () => {
    toasts.value.splice(0)
    timers.forEach(h => clearTimeout(h))
    timers.clear()
  }

  const show = (message: string, type: Toast['type'] = 'info', duration = 5000): string => {
    if (!message) return ''
    
    // Dedup: stesso messaggio negli ultimi 2s
    const now = Date.now()
    const dup = toasts.value.find(
      t => t.message === message && t.type === type && now - t.createdAt < 2000
    )
    if (dup) return dup.id

    // Antiflood
    while (toasts.value.length >= MAX_TOASTS) {
      remove(toasts.value[0].id)
    }

    const id = genId()
    const toast: Toast = { id, message, type, duration, createdAt: now }
    toasts.value.push(toast)

    const h = window.setTimeout(() => remove(id), duration)
    timers.set(id, h)
    return id
  }

  const success = (m: string, d = 5000) => show(m, 'success', d)
  const error = (m: string, d = 7000) => show(m, 'error', d)
  const warning = (m: string, d = 6000) => show(m, 'warning', d)
  const info = (m: string, d = 4000) => show(m, 'info', d)

  onBeforeUnmount(clearAll)

  return { 
    toasts, 
    show, 
    success, 
    error, 
    warning, 
    info, 
    remove, 
    clearAll 
  }
}




